import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import NotificationBadge from './NotificationBadge';
import LanguageSwitcher from './LanguageSwitcher';
import api from '../lib/api';
import {
  LayoutDashboard,
  Building2,
  Building,
  Users,
  Fingerprint,
  Calendar,
  Settings,
  DollarSign,
  BarChart3,
  LogOut,
  User,
  Umbrella,
  UserCog,
  FileText,
  ClipboardCheck,
  Shield,
  ScrollText,
  QrCode,
  TrendingUp,
  CheckSquare,
  Clock,
  Bell,
  Moon,
  Mail,
  FileSpreadsheet,
  SendHorizonal
} from 'lucide-react';

const menuItems = [
  { name: 'nav.dashboard', path: '/dashboard', icon: LayoutDashboard, roles: ['super_admin', 'company_admin', 'hr', 'director', 'group_admin', 'group_hr'] },
  { name: 'nav.companies', path: '/companies', icon: Building2, roles: ['super_admin'] },
  { name: 'nav.users', path: '/users', icon: UserCog, roles: ['super_admin', 'company_admin', 'group_admin'] },
  { name: 'nav.approvals', path: '/approvals', icon: CheckSquare, roles: ['director', 'company_admin', 'hr', 'group_admin', 'group_hr'], badge: 'approvals' },
  { name: 'nav.departments', path: '/departments', icon: Building, roles: ['company_admin', 'hr', 'director', 'group_admin', 'group_hr'] },
  { name: 'nav.employees', path: '/employees', icon: Users, roles: ['super_admin', 'company_admin', 'hr', 'director', 'group_admin', 'group_hr'] },
  { name: 'nav.scheduleExceptions', path: '/schedule-exceptions', icon: Clock, roles: ['company_admin', 'hr', 'director', 'group_admin', 'group_hr'] },
  { name: 'nav.zkteco', path: '/zkteco', icon: Fingerprint, roles: ['super_admin', 'company_admin', 'group_admin'] },
  { name: 'nav.alerts', path: '/alerts', icon: Bell, roles: ['super_admin', 'company_admin', 'hr', 'director', 'group_admin', 'group_hr'] },
  { name: 'nav.shifts', path: '/shifts', icon: Clock, roles: ['super_admin', 'company_admin', 'hr', 'director', 'group_admin', 'group_hr'] },
  { name: 'nav.qrAttendance', path: '/qr-attendance', icon: QrCode, roles: ['super_admin', 'company_admin', 'hr', 'group_admin', 'group_hr'] },
  { name: 'nav.attendance', path: '/attendance', icon: Calendar, roles: ['super_admin', 'company_admin', 'hr', 'director', 'group_admin', 'group_hr'], badge: 'justifications' },
  { name: 'nav.leaves', path: '/leaves', icon: Umbrella, roles: ['super_admin', 'company_admin', 'hr', 'director', 'group_admin', 'group_hr'], badge: 'leaves' },
  { name: 'nav.hrSettings', path: '/hr-settings', icon: Settings, roles: ['company_admin', 'hr', 'group_admin', 'group_hr'] },
  { name: 'nav.leaveTypes', path: '/leave-types', icon: Umbrella, roles: ['company_admin', 'hr', 'director', 'group_admin', 'group_hr'] },
  { name: 'nav.publicHolidays', path: '/public-holidays', icon: Calendar, roles: ['company_admin', 'hr', 'group_admin', 'group_hr'] },
  { name: 'nav.overtime', path: '/overtime', icon: TrendingUp, roles: ['super_admin', 'company_admin', 'hr', 'director', 'group_admin', 'group_hr'] },
  { name: 'nav.legalDeductions', path: '/legal-deductions', icon: Shield, roles: ['company_admin', 'hr', 'director', 'group_admin', 'group_hr'] },
  { name: 'nav.payroll', path: '/payroll', icon: DollarSign, roles: ['super_admin', 'company_admin', 'hr', 'group_admin', 'group_hr'] },
  { name: 'nav.payrollSimulator', path: '/payroll/simulate', icon: FileSpreadsheet, roles: ['super_admin', 'company_admin', 'hr', 'group_admin', 'group_hr'] },
  { name: 'nav.analytics', path: '/analytics', icon: TrendingUp, roles: ['super_admin', 'company_admin', 'hr', 'director', 'group_admin', 'group_hr'] },
  { name: 'nav.reports', path: '/reports', icon: BarChart3, roles: ['super_admin', 'company_admin', 'director', 'group_admin'] },
  { name: 'nav.justifications', path: '/justifications', icon: ClipboardCheck, roles: ['company_admin', 'hr', 'group_admin', 'group_hr'] },
  { name: 'nav.auditLogs', path: '/audit-logs', icon: ScrollText, roles: ['super_admin', 'company_admin', 'group_admin'] },
  { name: 'nav.smtpSettings', path: '/smtp-settings', icon: Mail, roles: ['super_admin', 'company_admin', 'group_admin'] },
  { name: 'nav.emailBroadcast', path: '/email-broadcast', icon: SendHorizonal, roles: ['super_admin', 'company_admin', 'hr', 'director', 'group_admin', 'group_hr'] },
];

const employeeMenuItems = [
  { name: 'nav.mySpace', path: '/employee/dashboard', icon: LayoutDashboard },
  { name: 'nav.myAttendances', path: '/employee/attendances', icon: Calendar },
  { name: 'nav.qrAttendance', path: '/employee/qr', icon: QrCode },
  { name: 'nav.myLeaves', path: '/employee/leaves', icon: Umbrella },
  { name: 'nav.myScheduleExceptions', path: '/employee/schedule-exceptions', icon: Clock },
  { name: 'nav.myShift', path: '/employee/shift', icon: Moon },
  { name: 'nav.mySalaryRequests', path: '/employee/salary-requests', icon: DollarSign },
  { name: 'nav.myPayslips', path: '/employee/payslips', icon: FileText },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [companyBranding, setCompanyBranding] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';
  const isGroupLevel = ['super_admin', 'group_admin', 'group_hr'].includes(user?.role);

  useEffect(() => {
    // Fetch company branding for non-group level users
    const fetchCompanyBranding = async () => {
      if (!isGroupLevel && user?.company_id) {
        try {
          const response = await api.get(`/companies/${user.company_id}`);
          setCompanyBranding({
            name: response.data.name,
            logo_url: response.data.logo_url
          });
        } catch (error) {
          console.error('Failed to fetch company branding:', error);
        }
      }
    };
    
    fetchCompanyBranding();
  }, [user?.company_id, isGroupLevel]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isEmployee = user?.role === 'employee';
  const displayMenuItems = isEmployee ? employeeMenuItems : menuItems.filter(item => item.roles.includes(user?.role));

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Logo path already includes /api/uploads prefix from backend
    return `${backendUrl}${logoPath}`;
  };

  // Determine what to display in the header
  const displayName = isGroupLevel ? 'LuxosGroup HR' : (companyBranding?.name || 'LuxosGroup HR');
  const displaySubtitle = isGroupLevel ? 'Gestion RH SaaS' : 'Portail RH';

  return (
    <aside className="w-64 bg-slate-900 text-slate-300 flex flex-col" data-testid="sidebar">
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          {!isGroupLevel && companyBranding?.logo_url ? (
            <img
              src={getLogoUrl(companyBranding.logo_url)}
              alt={companyBranding.name}
              className="w-10 h-10 rounded-lg object-contain bg-white p-1"
              data-testid="sidebar-company-logo"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-bold text-lg text-white truncate" data-testid="sidebar-company-name">
              {displayName}
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">{displaySubtitle}</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {displayMenuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                isActive
                  ? 'bg-primary text-white shadow-lg'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`
            }
            data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium flex-1">{t(item.name)}</span>
            {item.badge && !isEmployee && <NotificationBadge type={item.badge} />}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-800 space-y-2">
        <NavLink
          to="/security"
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-all"
          data-testid="nav-security"
        >
          <Shield className="w-5 h-5" />
          <span className="font-medium">{t('nav.security')}</span>
        </NavLink>
        
        <NavLink
          to="/profile"
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-all"
        >
          <User className="w-5 h-5" />
          <span className="font-medium">{t('nav.profile')}</span>
        </NavLink>
        
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start gap-3 text-slate-300 hover:bg-slate-800 hover:text-white"
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">{t('auth.logout')}</span>
        </Button>

        <div className="pt-3 px-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">{user?.full_name}</p>
            <p className="text-xs text-slate-400">{user?.email}</p>
          </div>
          <LanguageSwitcher variant="compact" />
        </div>
      </div>
    </aside>
  );
}